import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosConfig';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

const API = '/api/tenants';

const emptyForm = () => ({
  name: '',
  slug: '',
  primary_color: '#2b6cb0',
  secondary_color: '#111827',
  logo_url: '',
  allowed_origins: '',
  custom_css: '',
  widget_theme: 'default',
  public_chat_token: '',
  remove_public_chat_token: false,
});

const emptyBotForm = () => ({
  display_name: 'Chatbot',
  welcome_message: '',
  quick_start_messages: '[]',
  prompt_template_id: '',
  model_config_id: '',
  bubble_color: '#2b6cb0',
  position: 'bottom-right',
  knowledge_base_ids: '',
});

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [botOpen, setBotOpen] = useState(false);
  const [botTenantId, setBotTenantId] = useState(null);
  const [botForm, setBotForm] = useState(emptyBotForm());
  const [prompts, setPrompts] = useState([]);
  const [models, setModels] = useState([]);

  const load = async () => {
    const res = await axios.get(`${API}/`);
    setTenants(res.data);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/`, {
        name: form.name,
        slug: form.slug,
        primary_color: form.primary_color,
        secondary_color: form.secondary_color,
        logo_url: form.logo_url,
        allowed_origins: form.allowed_origins,
        custom_css: form.custom_css,
        widget_theme: form.widget_theme,
      });
      setOpen(false);
      setForm(emptyForm());
      await load();
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({
      name: t.name,
      slug: t.slug,
      primary_color: t.primary_color || '#2b6cb0',
      secondary_color: t.secondary_color || '#111827',
      logo_url: t.logo_url || '',
      allowed_origins: t.allowed_origins || '',
      custom_css: t.custom_css || '',
      widget_theme: t.widget_theme || 'default',
      public_chat_token: '',
      remove_public_chat_token: false,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const putPayload = {
        name: form.name,
        slug: form.slug,
        primary_color: form.primary_color,
        secondary_color: form.secondary_color,
        logo_url: form.logo_url,
        allowed_origins: form.allowed_origins,
        custom_css: form.custom_css,
        widget_theme: form.widget_theme,
      };
      if (form.remove_public_chat_token) {
        putPayload.public_chat_token = null;
      } else if (form.public_chat_token && form.public_chat_token.trim()) {
        putPayload.public_chat_token = form.public_chat_token.trim();
      }
      await axios.put(`${API}/${editingId}`, putPayload);
      setEditOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Mandant wirklich löschen?')) return;
    await axios.delete(`${API}/${id}`);
    await load();
  };

  const openBotDialog = async (tenantId) => {
    setBotTenantId(tenantId);
    setBotOpen(true);
    try {
      const [pr, mo, botRes] = await Promise.all([
        axios.get('/api/prompts/'),
        axios.get('/api/models/'),
        axios.get(`${API}/${tenantId}/bot`),
      ]);
      setPrompts(pr.data || []);
      setModels(mo.data || []);
      if (botRes.data.has_bot && botRes.data.bot) {
        const b = botRes.data.bot;
        setBotForm({
          display_name: b.display_name || 'Chatbot',
          welcome_message: b.welcome_message || '',
          quick_start_messages: b.quick_start_messages || '[]',
          prompt_template_id: b.prompt_template_id ?? '',
          model_config_id: b.model_config_id ?? '',
          bubble_color: b.bubble_color || '#2b6cb0',
          position: b.position || 'bottom-right',
          knowledge_base_ids: b.knowledge_base_ids || '',
        });
      } else {
        setBotForm(emptyBotForm());
      }
    } catch (e) {
      console.error(e);
      setBotForm(emptyBotForm());
    }
  };

  const saveBot = async () => {
    if (!botTenantId) return;
    setSaving(true);
    try {
      const payload = {
        display_name: botForm.display_name,
        welcome_message: botForm.welcome_message,
        quick_start_messages: botForm.quick_start_messages,
        bubble_color: botForm.bubble_color,
        position: botForm.position,
        knowledge_base_ids: botForm.knowledge_base_ids || null,
      };
      if (botForm.prompt_template_id !== '' && botForm.prompt_template_id != null) {
        payload.prompt_template_id = Number(botForm.prompt_template_id);
      } else {
        payload.prompt_template_id = null;
      }
      if (botForm.model_config_id !== '' && botForm.model_config_id != null) {
        payload.model_config_id = Number(botForm.model_config_id);
      } else {
        payload.model_config_id = null;
      }
      await axios.put(`${API}/${botTenantId}/bot`, payload);
      setBotOpen(false);
      setBotTenantId(null);
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  const themeFields = (
    <>
      <TextField
        select
        label="Widget-Theme"
        value={form.widget_theme}
        onChange={(e) => setForm({ ...form, widget_theme: e.target.value })}
        fullWidth
      >
        <MenuItem value="default">Standard</MenuItem>
        <MenuItem value="compact">Kompakt</MenuItem>
        <MenuItem value="drawer">Drawer (seitlich)</MenuItem>
        <MenuItem value="minimal">Minimal</MenuItem>
      </TextField>
    </>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Mandanten</Typography>
        <Button
          variant="contained"
          onClick={() => {
            setForm(emptyForm());
            setOpen(true);
          }}
        >
          Neuer Mandant
        </Button>
      </Box>

      <Grid container spacing={2}>
        {tenants.map((t) => (
          <Grid item xs={12} md={6} lg={4} key={t.id}>
            <Card>
              <CardContent>
                <Typography variant="h6">{t.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Slug: {t.slug}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  Theme: {t.widget_theme || 'default'}
                </Typography>
                {t.logo_url ? (
                  <Box sx={{ mt: 1 }}>
                    <img src={t.logo_url} alt="logo" style={{ maxWidth: '100%', maxHeight: 60 }} />
                  </Box>
                ) : null}
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => startEdit(t)}>
                  Bearbeiten
                </Button>
                <Button size="small" onClick={() => openBotDialog(t.id)}>
                  Chatbot
                </Button>
                <Button size="small" color="error" onClick={() => remove(t.id)}>
                  Löschen
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Neuer Mandant</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <TextField label="Slug (URL)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} fullWidth />
            <TextField label="Primärfarbe" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} fullWidth />
            <TextField label="Sekundärfarbe" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} fullWidth />
            <TextField label="Logo URL" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} fullWidth />
            <TextField label="Erlaubte Origins (kommagetrennt oder *)" value={form.allowed_origins} onChange={(e) => setForm({ ...form, allowed_origins: e.target.value })} fullWidth />
            <TextField label="Custom CSS" value={form.custom_css} onChange={(e) => setForm({ ...form, custom_css: e.target.value })} fullWidth multiline minRows={4} />
            {themeFields}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={create} disabled={saving} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Mandant bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <TextField label="Slug (URL)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} fullWidth />
            <TextField label="Primärfarbe" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} fullWidth />
            <TextField label="Sekundärfarbe" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} fullWidth />
            <TextField label="Logo URL" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} fullWidth />
            <TextField label="Erlaubte Origins" value={form.allowed_origins} onChange={(e) => setForm({ ...form, allowed_origins: e.target.value })} fullWidth />
            <TextField label="Custom CSS" value={form.custom_css} onChange={(e) => setForm({ ...form, custom_css: e.target.value })} fullWidth multiline minRows={4} />
            <Alert severity="info" sx={{ py: 0.5 }}>
              Optional: <strong>Öffentliches Chat-Token</strong> für Server-zu-Server (<code>POST /api/&lt;slug&gt;/chat</code>): Header{' '}
              <code>X-Kichatbot-Token</code> oder JSON-Feld <code>chat_token</code>. Das eingebettete Widget erhält das Token automatisch.
            </Alert>
            <TextField
              type="password"
              autoComplete="new-password"
              label="Neues öffentliches Chat-Token"
              value={form.public_chat_token}
              onChange={(e) => setForm({ ...form, public_chat_token: e.target.value })}
              fullWidth
              helperText="Leer lassen, um das bestehende Token beizubehalten."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.remove_public_chat_token}
                  onChange={(e) => setForm({ ...form, remove_public_chat_token: e.target.checked })}
                />
              }
              label="Gespeichertes Chat-Token entfernen"
            />
            {themeFields}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Abbrechen</Button>
          <Button onClick={saveEdit} disabled={saving} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={botOpen} onClose={() => setBotOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Mandanten-Chatbot (Widget)</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info">
              Steuert den öffentlichen Tenant-Chat unter <code>/api/&lt;slug&gt;/widget</code>. Quick-Replies als JSON-Array, z. B.{' '}
              <code>["E-Bike Beratung","Werkstatt"]</code>.
            </Alert>
            <TextField
              label="Anzeigename"
              value={botForm.display_name}
              onChange={(e) => setBotForm({ ...botForm, display_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Begrüßung"
              value={botForm.welcome_message}
              onChange={(e) => setBotForm({ ...botForm, welcome_message: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Quick-Start (JSON-Array)"
              value={botForm.quick_start_messages}
              onChange={(e) => setBotForm({ ...botForm, quick_start_messages: e.target.value })}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              select
              label="Prompt-Vorlage"
              value={botForm.prompt_template_id === '' || botForm.prompt_template_id == null ? '' : botForm.prompt_template_id}
              onChange={(e) =>
                setBotForm({
                  ...botForm,
                  prompt_template_id: e.target.value === '' ? '' : e.target.value,
                })
              }
              fullWidth
            >
              <MenuItem value="">
                <em>Keine</em>
              </MenuItem>
              {(prompts || []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Modell-Konfiguration"
              value={botForm.model_config_id === '' || botForm.model_config_id == null ? '' : botForm.model_config_id}
              onChange={(e) =>
                setBotForm({
                  ...botForm,
                  model_config_id: e.target.value === '' ? '' : e.target.value,
                })
              }
              fullWidth
            >
              <MenuItem value="">
                <em>Keine</em>
              </MenuItem>
              {(models || []).map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name} ({m.provider})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Wissensbasis-IDs (kommagetrennt)"
              value={botForm.knowledge_base_ids}
              onChange={(e) => setBotForm({ ...botForm, knowledge_base_ids: e.target.value })}
              fullWidth
              placeholder="1,2,3"
            />
            <TextField
              label="Sprechblasen-Farbe"
              value={botForm.bubble_color}
              onChange={(e) => setBotForm({ ...botForm, bubble_color: e.target.value })}
              fullWidth
            />
            <TextField select label="Button-Position" value={botForm.position} onChange={(e) => setBotForm({ ...botForm, position: e.target.value })} fullWidth>
              <MenuItem value="bottom-right">Unten rechts</MenuItem>
              <MenuItem value="bottom-left">Unten links</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBotOpen(false)}>Abbrechen</Button>
          <Button onClick={saveBot} disabled={saving} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tenants;
